import { useParams, Link } from "react-router-dom";
import { Helmet } from "react-helmet";
import Navbar from "@/components/layout/Navbar";
import Footer from "@/components/layout/Footer";
import { useHomeCareProvider } from "@/hooks/use-home-care-provider";
import { HomeCareEnquiryForm } from "@/components/home-care/HomeCareEnquiryForm";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Separator } from "@/components/ui/separator";
import {
  MapPin, Phone, Globe, Star, ShieldCheck, Clock, Building2,
  Calendar, Languages, Heart, Moon, Home, Bed, ChevronRight,
} from "lucide-react";

// Treat "null" strings from DB as empty
const val = (v: string | null | undefined): string | null =>
  v && v !== "null" ? v : null;

export default function HomeCareProviderPage() {
  const { county, slug } = useParams<{ county: string; slug: string }>();
  const { data: provider, isLoading, error } = useHomeCareProvider(county || "", slug || "");

  if (isLoading) {
    return (
      <>
        <Navbar />
        <main className="min-h-screen bg-background">
          <div className="container py-20 text-center text-muted-foreground">Loading provider details…</div>
        </main>
        <Footer />
      </>
    );
  }

  if (error || !provider) {
    return (
      <>
        <Navbar />
        <main className="min-h-screen bg-background">
          <div className="container py-20 text-center">
            <h1 className="text-2xl font-bold text-foreground mb-2">Provider Not Found</h1>
            <p className="text-muted-foreground mb-4">We couldn't find this home care provider.</p>
            <Link to="/home-care" className="text-primary hover:underline">← Back to Home Care Directory</Link>
          </div>
        </main>
        <Footer />
      </>
    );
  }

  const rateDisplay =
    provider.hourly_rate_min && provider.hourly_rate_max
      ? `€${provider.hourly_rate_min} – €${provider.hourly_rate_max}/hr`
      : provider.hourly_rate_min
        ? `From €${provider.hourly_rate_min}/hr`
        : null;

  const boolFeatures = [
    { label: "Live-in Care", value: provider.offers_live_in_care, icon: Home },
    { label: "Overnight Care", value: provider.offers_overnight_care, icon: Moon },
    { label: "Respite Care", value: provider.offers_respite_care, icon: Heart },
    { label: "Emergency Service", value: provider.emergency_service, icon: Clock },
    { label: "Home Care Packages", value: provider.accepts_home_care_packages, icon: Bed },
  ].filter((f) => f.value);

  return (
    <>
      <Helmet>
        <title>{`${provider.name} – Home Care in ${provider.county_name} | Grandpal`}</title>
        <meta
          name="description"
          content={provider.meta_description || provider.short_description || `Find out about ${provider.name}, a home care provider in ${provider.county_name}, Ireland.`}
        />
      </Helmet>
      <Navbar />
      <main className="min-h-screen bg-background">
        {/* Breadcrumb */}
        <div className="border-b bg-muted/30">
          <div className="container py-3 flex items-center gap-1.5 text-sm text-muted-foreground">
            <Link to="/home-care" className="hover:text-primary transition-colors">Home Care</Link>
            <ChevronRight className="h-3.5 w-3.5" />
            <Link to={`/home-care?county=${provider.county_slug}`} className="hover:text-primary transition-colors capitalize">
              {provider.county_name}
            </Link>
            <ChevronRight className="h-3.5 w-3.5" />
            <span className="text-foreground font-medium truncate">{provider.name}</span>
          </div>
        </div>

        <div className="container py-8 lg:py-12">
          <div className="grid lg:grid-cols-3 gap-8">
            {/* Main Content */}
            <div className="lg:col-span-2 space-y-8">
              {/* Header */}
              <div>
                <div className="flex flex-wrap items-center gap-2 mb-2">
                  {provider.is_hse_approved && (
                    <Badge variant="secondary" className="gap-1 bg-accent text-accent-foreground">
                      <ShieldCheck className="h-3.5 w-3.5" /> HSE Approved
                    </Badge>
                  )}
                  {provider.is_verified && (
                    <Badge variant="outline" className="gap-1">Verified</Badge>
                  )}
                  {provider.rating > 0 && (
                    <Badge variant="outline" className="gap-1 text-primary border-primary/30">
                      <Star className="h-3.5 w-3.5 fill-primary" />
                      {Number(provider.rating).toFixed(1)}
                      {provider.review_count > 0 && ` (${provider.review_count} reviews)`}
                    </Badge>
                  )}
                </div>
                <h1 className="text-3xl font-bold text-foreground font-lora">{provider.name}</h1>
                <div className="flex items-center gap-1.5 text-muted-foreground mt-1">
                  <MapPin className="h-4 w-4 shrink-0" />
                  <span>{val(provider.address) ? `${provider.address}, ` : ""}{provider.county_name}</span>
                  {provider.eir_code && <span className="text-xs">({provider.eir_code})</span>}
                </div>
              </div>

              {/* Description */}
              {provider.description && (
                <div>
                  <h2 className="text-xl font-semibold text-foreground mb-3">About</h2>
                  <p className="text-muted-foreground leading-relaxed whitespace-pre-line">{provider.description}</p>
                </div>
              )}

              {/* Services */}
              {provider.services?.length > 0 && (
                <div>
                  <h2 className="text-xl font-semibold text-foreground mb-3">Services</h2>
                  <div className="flex flex-wrap gap-2">
                    {provider.services.map((s: string) => (
                      <Badge key={s} variant="outline" className="text-sm font-normal py-1 px-3">{s}</Badge>
                    ))}
                  </div>
                </div>
              )}

              {/* Specializations */}
              {provider.specializations?.length > 0 && (
                <div>
                  <h2 className="text-xl font-semibold text-foreground mb-3">Specializations</h2>
                  <div className="flex flex-wrap gap-2">
                    {provider.specializations.map((s: string) => (
                      <Badge key={s} className="text-sm font-normal py-1 px-3 bg-primary/10 text-primary border-primary/20">{s}</Badge>
                    ))}
                  </div>
                </div>
              )}

              {/* Care Options */}
              {boolFeatures.length > 0 && (
                <div>
                  <h2 className="text-xl font-semibold text-foreground mb-3">Care Options</h2>
                  <div className="grid sm:grid-cols-2 gap-3">
                    {boolFeatures.map((f) => (
                      <div key={f.label} className="flex items-center gap-2 p-3 rounded-lg bg-muted/50 border">
                        <f.icon className="h-4 w-4 text-primary shrink-0" />
                        <span className="text-sm font-medium text-foreground">{f.label}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Coverage Areas */}
              {provider.coverage_areas?.length > 0 && (
                <div>
                  <h2 className="text-xl font-semibold text-foreground mb-3">Coverage Areas</h2>
                  <div className="flex flex-wrap gap-2">
                    {provider.coverage_areas.map((area: string) => (
                      <Badge key={area} variant="secondary" className="text-sm font-normal">{area}</Badge>
                    ))}
                  </div>
                  {provider.covers_full_county && (
                    <p className="text-sm text-muted-foreground mt-2">Covers the full county of {provider.county_name}.</p>
                  )}
                </div>
              )}

              {/* Languages */}
              {provider.languages_spoken?.length > 0 && (
                <div className="flex items-center gap-2 text-sm text-muted-foreground">
                  <Languages className="h-4 w-4" />
                  <span>Languages: {provider.languages_spoken.join(", ")}</span>
                </div>
              )}
            </div>

            {/* Sidebar */}
            <div className="space-y-6">
              {/* Contact Card */}
              <Card>
                <CardHeader className="pb-3">
                  <CardTitle className="text-lg">Contact Details</CardTitle>
                </CardHeader>
                <CardContent className="space-y-3">
                  {val(provider.phone) && (
                    <a href={`tel:${provider.phone}`} className="flex items-center gap-2 text-sm text-primary hover:underline">
                      <Phone className="h-4 w-4" /> {provider.phone}
                    </a>
                  )}
                  {val(provider.email) && (
                    <a href={`mailto:${provider.email}`} className="flex items-center gap-2 text-sm text-primary hover:underline break-all">
                      <span className="shrink-0">✉️</span> {provider.email}
                    </a>
                  )}
                  {val(provider.website) && (
                    <a href={provider.website} target="_blank" rel="noopener noreferrer" className="flex items-center gap-2 text-sm text-primary hover:underline break-all">
                      <Globe className="h-4 w-4 shrink-0" /> Visit website
                    </a>
                  )}
                  {rateDisplay && (
                    <div className="flex items-center gap-2 text-sm text-muted-foreground">
                      <Clock className="h-4 w-4" /> {rateDisplay}
                    </div>
                  )}
                  {val(provider.minimum_visit_duration) && (
                    <div className="flex items-center gap-2 text-sm text-muted-foreground">
                      <Clock className="h-4 w-4" /> Min visit: {provider.minimum_visit_duration}
                    </div>
                  )}
                  {val(provider.operating_hours) && (
                    <div className="flex items-center gap-2 text-sm text-muted-foreground">
                      <Calendar className="h-4 w-4" /> {provider.operating_hours}
                    </div>
                  )}
                  {provider.year_established && (
                    <div className="flex items-center gap-2 text-sm text-muted-foreground">
                      <Building2 className="h-4 w-4" /> Est. {provider.year_established}
                    </div>
                  )}
                  {val(provider.registration_number) && (
                    <div className="text-xs text-muted-foreground mt-2">
                      Reg. #{provider.registration_number}
                    </div>
                  )}
                </CardContent>
              </Card>

              {/* Enquiry Form */}
              <HomeCareEnquiryForm
                providerName={provider.name}
                providerSlug={provider.slug}
                countyName={provider.county_name}
              />
            </div>
          </div>
        </div>
      </main>
      <Footer />
    </>
  );
}
